import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Paper,
  Chip,
} from '@mui/material';
import {
  Close,
  NavigateBefore,
  NavigateNext,
  Fullscreen,
  ZoomIn,
} from '@mui/icons-material';

interface ImageGalleryProps {
  images: string[];
  title: string;
  open: boolean;
  onClose: () => void;
  initialIndex?: number;
  /** Optional per-image caption shown under the counter (e.g. Storage vs CDN). */
  captions?: Array<string | undefined>;
}

const toUrl = (img: string | { url?: string } | undefined): string | undefined => {
  if (!img) return undefined;
  const u = typeof img === 'string' ? img : img?.url;
  return typeof u === 'string' ? (u.startsWith('//') ? `https:${u}` : u) : undefined;
};

export const ImageGallery: React.FC<ImageGalleryProps> = ({
  images,
  title,
  open,
  onClose,
  initialIndex = 0,
  captions,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (open) {
      const max = Math.max(0, images.length - 1);
      setCurrentIndex(Math.min(Math.max(0, initialIndex), max));
      setIsFullscreen(false);
    }
  }, [open, initialIndex, images.length]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowLeft') {
      handlePrevious();
    } else if (event.key === 'ArrowRight') {
      handleNext();
    } else if (event.key === 'Escape') {
      onClose();
    }
  };

  const currentImage = images[currentIndex];
  const imageUrl = toUrl(currentImage);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isFullscreen}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <DialogContent
        sx={{
          p: 0,
          position: 'relative',
          backgroundColor: '#000',
          minHeight: isFullscreen ? '100vh' : '70vh',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 2,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)',
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ minWidth: 0, pr: 1 }}>
            <Typography variant="h6" color="white" sx={{ fontWeight: 'bold' }} noWrap>
              {title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
              <Chip
                label={`${currentIndex + 1} of ${images.length}`}
                size="small"
                sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
              {captions?.[currentIndex] ? (
                <Chip
                  label={captions[currentIndex]}
                  size="small"
                  sx={{ backgroundColor: 'rgba(255,255,255,0.15)', color: 'white' }}
                />
              ) : null}
            </Box>
            {imageUrl ? (
              <Typography
                variant="caption"
                color="rgba(255,255,255,0.75)"
                component="a"
                href={imageUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'block',
                  mt: 0.5,
                  maxWidth: { xs: 240, sm: 480 },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  textDecoration: 'underline',
                  color: 'rgba(255,255,255,0.75)',
                }}
              >
                {imageUrl}
              </Typography>
            ) : null}
          </Box>
          <Box>
            <IconButton
              onClick={() => setIsFullscreen(!isFullscreen)}
              sx={{ color: 'white', mr: 1 }}
            >
              {isFullscreen ? <ZoomIn /> : <Fullscreen />}
            </IconButton>
            <IconButton onClick={onClose} sx={{ color: 'white' }}>
              <Close />
            </IconButton>
          </Box>
        </Box>

        {/* Main Image */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            position: 'relative',
          }}
        >
          <img
            src={imageUrl || '/placeholder-camera.svg'}
            alt={`${title} - Image ${currentIndex + 1}`}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
            }}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = '/placeholder-camera.svg'; // Fallback image
            }}
          />
        </Box>

        {/* Navigation Arrows */}
        {images.length > 1 && (
          <>
            <IconButton
              onClick={handlePrevious}
              sx={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.7)',
                },
              }}
            >
              <NavigateBefore />
            </IconButton>
            <IconButton
              onClick={handleNext}
              sx={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.7)',
                },
              }}
            >
              <NavigateNext />
            </IconButton>
          </>
        )}

        {/* Thumbnail Strip */}
        {images.length > 1 && !isFullscreen && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
              p: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                overflowX: 'auto',
                justifyContent: 'center',
              }}
            >
              {images.map((image, index) => (
                <Paper
                  key={index}
                  sx={{
                    minWidth: 60,
                    height: 60,
                    cursor: 'pointer',
                    border: '2px solid',
                    borderColor: currentIndex === index ? 'primary.main' : 'transparent',
                    overflow: 'hidden',
                  }}
                  onClick={() => setCurrentIndex(index)}
                >
                  <img
                    src={toUrl(image) || ''}
                    alt={`Thumbnail ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = '/placeholder-camera.svg';
                    }}
                  />
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        {/* Keyboard Instructions */}
        {images.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: images.length > 1 && !isFullscreen ? 80 : 16,
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(0,0,0,0.7)',
              color: 'white',
              px: 2,
              py: 1,
              borderRadius: 1,
              fontSize: '0.75rem',
            }}
          >
            Use arrow keys to navigate • Press ESC to close
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
